import type { CSSProperties, ReactNode } from "react";
import "./document-model.css";
import type {
  AdmittedDocumentReactRenderInput,
  AdmittedDocumentRenderNode,
  DocumentChartData,
  DocumentTextStyle,
} from "./document-model-types";

const DEFAULT_FONT_FAMILY = '"Noto Sans KR", "Noto Sans", sans-serif';
const CHART_COLORS = ["#3366CC", "#DC3912", "#FF9900", "#109618"];

export function DocumentModelRenderer({
  admitted,
}: {
  admitted: AdmittedDocumentReactRenderInput;
}) {
  return (
    <div className="cogo-document-model" data-testid="document-model">
      {admitted.document.pages.map((page) => (
        <DocumentModelPage
          key={`${page.pageIndex}:${page.id}`}
          admitted={admitted}
          pageIndex={page.pageIndex}
        />
      ))}
    </div>
  );
}

export function DocumentModelPage({
  admitted,
  pageIndex,
}: {
  admitted: AdmittedDocumentReactRenderInput;
  pageIndex: number;
}) {
  const page = admitted.document.pages.find(
    (candidate) => candidate.pageIndex === pageIndex,
  );
  if (!page) return null;

  const pageStyle: CSSProperties = {
    position: "relative",
    overflow: "hidden",
    boxSizing: "border-box",
    background: "#ffffff",
    color: "#000000",
    width: page.width,
    minWidth: page.width,
    maxWidth: page.width,
    height: page.height,
    minHeight: page.height,
    maxHeight: page.height,
  };

  return (
    <section
      className="cogo-document-page"
      data-page-index={page.pageIndex}
      data-testid={`document-page-${page.pageIndex}`}
      style={pageStyle}
    >
      {page.nodes.map((node, sourceIndex) =>
        renderNode(node, sourceIndex, admitted),
      )}
    </section>
  );
}

function renderNode(
  node: AdmittedDocumentRenderNode,
  sourceIndex: number,
  admitted: AdmittedDocumentReactRenderInput,
): ReactNode {
  const style = nodeStyle(node, sourceIndex);
  const testId = `document-node-${node.kind}-${node.id}`;
  switch (node.kind) {
    case "paragraph":
    case "page_number":
      return (
        <div
          key={node.id}
          className="cogo-document-node cogo-document-text"
          data-testid={testId}
          style={{ ...style, ...textStyle(node.style) }}
        >
          {node.text}
        </div>
      );
    case "table":
      return (
        <div
          key={node.id}
          className="cogo-document-node cogo-document-table"
          data-testid={testId}
          data-row-count={node.rowCount}
          data-column-count={node.columnCount}
          style={{
            ...style,
            display: "grid",
            gridTemplateRows: `repeat(${node.rowCount}, minmax(0, 1fr))`,
            gridTemplateColumns: `repeat(${node.columnCount}, minmax(0, 1fr))`,
          }}
        >
          {node.cells.map((cell, index) => (
            <div
              key={`${cell.rowIndex}:${cell.columnIndex}:${index}`}
              className="cogo-document-table-cell"
              data-testid={`document-table-cell-${node.id}-${cell.rowIndex}-${cell.columnIndex}`}
              style={{
                gridRow: `${cell.rowIndex + 1} / span ${cell.rowSpan}`,
                gridColumn: `${cell.columnIndex + 1} / span ${cell.columnSpan}`,
                ...textStyle(cell.style),
              }}
            >
              {cell.text}
            </div>
          ))}
        </div>
      );
    case "picture": {
      const resource = admitted.resources[node.binaryItemIDRef];
      return (
        <img
          key={node.id}
          className="cogo-document-node cogo-document-picture"
          data-testid={testId}
          data-resource-id={resource.id}
          data-resource-sha256={resource.sha256}
          src={resource.dataUrl}
          alt=""
          style={{ ...style, objectFit: "fill" }}
        />
      );
    }
    case "shape":
      return renderShape(node, testId, style);
    case "chart":
      return (
        <div
          key={node.id}
          className="cogo-document-node cogo-document-chart"
          data-testid={testId}
          style={style}
        >
          {renderChart(node.chartData)}
        </div>
      );
  }
}

function renderShape(
  node: Extract<AdmittedDocumentRenderNode, { kind: "shape" }>,
  testId: string,
  style: CSSProperties,
) {
  const dash =
    node.stroke.dash === "solid"
      ? undefined
      : node.stroke.dash === "dash"
        ? "8 4"
        : "2 3";
  if (node.shapeType === "line") {
    return (
      <svg
        key={node.id}
        className="cogo-document-node cogo-document-shape"
        data-testid={testId}
        data-shape-type={node.shapeType}
        style={style}
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <line
          x1="0"
          y1="50"
          x2="100"
          y2="50"
          stroke={node.stroke.color}
          strokeWidth={node.stroke.widthPx}
          strokeOpacity={node.stroke.opacity}
          strokeDasharray={dash}
          vectorEffect="non-scaling-stroke"
        />
      </svg>
    );
  }
  return (
    <div
      key={node.id}
      className="cogo-document-node cogo-document-shape"
      data-testid={testId}
      data-shape-type={node.shapeType}
      style={{
        ...style,
        ...textStyle(node.style),
        backgroundColor: colorWithOpacity(node.fill.color, node.fill.opacity),
        borderColor: colorWithOpacity(node.stroke.color, node.stroke.opacity),
        borderWidth: node.stroke.widthPx,
        borderStyle:
          node.stroke.dash === "solid"
            ? "solid"
            : node.stroke.dash === "dash"
              ? "dashed"
              : "dotted",
        borderRadius: node.shapeType === "ellipse" ? "50%" : 0,
      }}
    >
      {node.text}
    </div>
  );
}

function renderChart(data: DocumentChartData) {
  const label = chartLabel(data);
  if (data.type === "bar") return renderBarChart(data, label);
  if (data.type === "line") return renderLineChart(data, label);
  return renderPieChart(data, label);
}

function renderBarChart(data: DocumentChartData, label: string) {
  const entries = data.series.flatMap((series, seriesIndex) =>
    series.values.map((value, categoryIndex) => ({
      value,
      series,
      seriesIndex,
      categoryIndex,
    })),
  );
  const max = Math.max(1, ...entries.map(({ value }) => Math.abs(value)));
  const slot = 840 / Math.max(1, entries.length);
  return (
    <svg
      viewBox="0 0 1000 1000"
      preserveAspectRatio="none"
      role="img"
      aria-label={label}
    >
      <title>{label}</title>
      {entries.map(({ value, series, seriesIndex, categoryIndex }, index) => {
        const height = (Math.abs(value) / max) * 800;
        return (
          <rect
            key={`${seriesIndex}:${categoryIndex}`}
            x={80 + index * slot + slot * 0.1}
            y={900 - height}
            width={slot * 0.8}
            height={height}
            fill={
              series.color ?? CHART_COLORS[seriesIndex % CHART_COLORS.length]
            }
          />
        );
      })}
    </svg>
  );
}

function renderLineChart(data: DocumentChartData, label: string) {
  const max = Math.max(
    1,
    ...data.series.flatMap((series) => series.values.map(Math.abs)),
  );
  return (
    <svg
      viewBox="0 0 1000 1000"
      preserveAspectRatio="none"
      role="img"
      aria-label={label}
    >
      <title>{label}</title>
      {data.series.map((series, seriesIndex) => {
        const divisor = Math.max(1, series.values.length - 1);
        const path = series.values
          .map((value, index) => {
            const x = 80 + (index / divisor) * 840;
            const y = 900 - (value / max) * 800;
            return `${index === 0 ? "M" : "L"} ${x} ${y}`;
          })
          .join(" ");
        return (
          <path
            key={series.name}
            d={path}
            fill="none"
            stroke={
              series.color ?? CHART_COLORS[seriesIndex % CHART_COLORS.length]
            }
            strokeWidth="8"
            vectorEffect="non-scaling-stroke"
          />
        );
      })}
    </svg>
  );
}

function renderPieChart(data: DocumentChartData, label: string) {
  const series = data.series[0];
  const values = series?.values ?? [];
  const total = values.reduce((sum, value) => sum + Math.max(0, value), 0);
  let angle = -Math.PI / 2;
  return (
    <svg
      viewBox="0 0 1000 1000"
      preserveAspectRatio="none"
      role="img"
      aria-label={label}
    >
      <title>{label}</title>
      {values.map((value, index) => {
        const sweep =
          total === 0 ? 0 : (Math.max(0, value) / total) * Math.PI * 2;
        const start = angle;
        angle += sweep;
        const largeArc = sweep > Math.PI ? 1 : 0;
        const x1 = 500 + 400 * Math.cos(start);
        const y1 = 500 + 400 * Math.sin(start);
        const x2 = 500 + 400 * Math.cos(angle);
        const y2 = 500 + 400 * Math.sin(angle);
        const d =
          sweep >= Math.PI * 2
            ? "M 500 100 A 400 400 0 1 1 499.99 100 Z"
            : `M 500 500 L ${x1} ${y1} A 400 400 0 ${largeArc} 1 ${x2} ${y2} Z`;
        return (
          <path
            key={index}
            d={d}
            fill={series?.color ?? CHART_COLORS[index % CHART_COLORS.length]}
          />
        );
      })}
    </svg>
  );
}

function chartLabel(data: DocumentChartData): string {
  const seriesNames = data.series.map((series) => series.name).join(", ");
  const values = data.categories
    .map((category, categoryIndex) => {
      const categoryValues = data.series
        .map((series) => series.values[categoryIndex])
        .join(", ");
      return `${category} ${categoryValues}`;
    })
    .join(", ");
  return `${data.type} chart: ${seriesNames}; ${values}`;
}

function colorWithOpacity(color: string, opacity: number): string {
  const red = Number.parseInt(color.slice(1, 3), 16);
  const green = Number.parseInt(color.slice(3, 5), 16);
  const blue = Number.parseInt(color.slice(5, 7), 16);
  return `rgba(${red}, ${green}, ${blue}, ${opacity})`;
}

function nodeStyle(
  node: AdmittedDocumentRenderNode,
  sourceIndex: number,
): CSSProperties {
  return {
    position: "absolute",
    boxSizing: "border-box",
    left: node.frame.x,
    top: node.frame.y,
    width: node.frame.width,
    height: node.frame.height,
    zIndex: sourceIndex,
  };
}

function textStyle(style?: DocumentTextStyle): CSSProperties {
  return {
    fontFamily: style?.fontFamily
      ? `"${style.fontFamily}", "Noto Sans", sans-serif`
      : DEFAULT_FONT_FAMILY,
    fontSize: style?.fontSizePx ?? 16,
    fontWeight: style?.fontWeight ?? 400,
    fontStyle: style?.fontStyle ?? "normal",
    color: style?.color ?? "#000000",
    lineHeight: style?.lineHeightPx ? `${style.lineHeightPx}px` : "normal",
    textAlign: style?.textAlign,
  };
}
